/** 报告族的注册表（`hummingbot/condor` 的「Reports 一等路由」形状）。
 *
 * condor 的做法值得抄的是：报告不是某个屏内部的产物，而是**有 id、有取数函数、
 * 有自己的路由**的一等公民。这里把形状先立起来，注册表里现在只有一条真的
 * （运行摘要，来自 `GET /api/v1/runs/{id}`），其余按卡推进——
 * **注册表为空不代表屏上可以填占位数据**。
 */

export interface ReportDescriptor<T> {
  id: string;
  title: string;
  /** 路由（哈希形式，沿用原型命名）。 */
  route: string;
  /** 这条报告靠哪个接口活着。 */
  source: string;
  /** 依赖的 Should / 卡；未开卡的不许实现。 */
  requires: string;
  /** 取数函数；未接线的报告不给。 */
  load?: () => Promise<T>;
}

export interface RunSummaryReport {
  run_id: string;
  orders: number;
  fills: number;
  realized_pnl: number;
  balanced: boolean;
  ledger_digest: string;
}

export const REPORTS: ReportDescriptor<unknown>[] = [
  {
    id: "run-summary",
    title: "运行摘要",
    route: "#replay",
    source: "GET /api/v1/runs/{run_id}",
    requires: "S2 trade-attribution（active）",
  },
  {
    id: "daily",
    title: "每日报告",
    route: "#reports",
    source: "—（接口未定义）",
    requires: "S6 daily-ops-report（planned，未开卡不许实现）",
  },
  {
    id: "risk",
    title: "风险核对报告",
    route: "#riskreport",
    source: "—（接口未定义）",
    requires: "S3 risk-limits（planned，未开卡不许实现）",
  },
];

export function reportById(id: string): ReportDescriptor<unknown> | undefined {
  return REPORTS.find((report) => report.id === id);
}

/** 某条路由上登记的报告——未接线屏据此说明「这里将来是什么报告」。 */
export function reportsForRoute(route: string): ReportDescriptor<unknown>[] {
  return REPORTS.filter((report) => report.route === route);
}

/** 已接线（有取数函数）的报告。屏上据此区分「能看」与「等接口」。 */
export function wiredReports(): ReportDescriptor<unknown>[] {
  return REPORTS.filter((report) => report.load !== undefined);
}

export interface PnlDisplay {
  total: number | undefined;
  realizedClosed: number | undefined;
  unrealized: number | undefined;
}

export function pnlDisplay(pnl: { total_pnl?: number; realized_closed?: number; unrealized?: number; cash_change?: number }): PnlDisplay {
  const total = pnl.total_pnl ?? pnl.cash_change;
  const realizedClosed = pnl.realized_closed ?? pnl.cash_change;
  return {
    total,
    realizedClosed,
    unrealized: pnl.unrealized ?? (total === undefined || realizedClosed === undefined ? undefined : total - realizedClosed),
  };
}

export function equityDisplay(point: { total_pnl?: number; realized_pnl_closed?: number; cash_change?: number; pnl?: number }): PnlDisplay {
  return pnlDisplay({
    total_pnl: point.total_pnl ?? point.pnl,
    realized_closed: point.realized_pnl_closed,
    cash_change: point.cash_change,
  });
}
